import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Spin,
  Select,
  Space,
  InputNumber,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const ProductVariations = () => {
  const userRole = localStorage.getItem("userRole");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [fabrics, setFabrics] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [allFabricColors, setAllFabricColors] = useState([]);
  const [allowedFabricColors, setAllowedFabricColors] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("id-desc");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // ---------- data loaders ----------
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        "/admin/get-products-with-variations"
      );
      if (Array.isArray(data.products)) {
        const mapped = data.products.map((p) => ({
          ...p,
          variationCount: parseInt(p.variations_count, 10) || 0,
        }));
        setProducts(mapped);
        setPagination((prev) => ({ ...prev, total: mapped.length }));
      } else {
        setProducts([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch {
      message.error("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  // 🔧 FIX: tolerate different API shapes and fallback to global list
  const fetchFabricsForProduct = async (productId) => {
    try {
      let fabricsArr = [];
      try {
        const { data } = await axiosInstance.get(
          `/admin/product-fabric/${productId}`
        );
        fabricsArr =
          data?.Fabrics ||
          data?.fabrics ||
          data?.data?.Fabrics ||
          data?.data?.fabrics ||
          [];
      } catch {
        // ignore and try global below
      }

      // If nothing product-specific, fall back to global fabrics-with-colors
      if (!Array.isArray(fabricsArr) || fabricsArr.length === 0) {
        const global = await axiosInstance.get(`/admin/fabrics-with-colors`);
        fabricsArr = global?.data?.fabrics || [];
      }

      const mapped = (fabricsArr || []).map((f) => ({
        label: f.fabric_name,
        value: f.id,
        image_url: f.image_url,
        // normalize colors key
        fabricColors: Array.isArray(f.fabricColors)
          ? f.fabricColors
          : Array.isArray(f.colors)
          ? f.colors
          : [],
      }));

      setFabrics(mapped);

      const allCols = [];
      mapped.forEach((f) => {
        (f.fabricColors || []).forEach((c) => {
          allCols.push({ ...c, fabricId: f.value });
        });
      });
      setAllFabricColors(allCols);
    } catch (error) {
      console.error("Error fetching fabrics:", error);
      message.error("Error fetching fabrics.");
      setFabrics([]);
      setAllFabricColors([]);
    }
  };

  const fetchSizesFromBackend = async () => {
    try {
      const { data } = await axiosInstance.get("/product/sizes");
      if (Array.isArray(data.sizes)) {
        setSizes(data.sizes.map((s) => ({ label: s.size, value: s.id })));
      } else {
        setSizes([]);
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
      setSizes([]);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (location.state?.updatedProduct) {
      fetchProducts();
      navigate("/products", { replace: true });
    }
  }, [location.state, navigate]);

  // ---------- actions ----------
  const handleViewVariations = async (product) => {
    try {
      const { data } = await axiosInstance.get(
        `/admin/product/variations/${product.id}`
      );
    if (data.success) {
        const count =
          data.variationCount ??
          (Array.isArray(data.variations) ? data.variations.length : 0);
        product.variationCount = count;
        if (count > 0) {
          navigate(`/product-variations/${product.id}`, {
            state: { product },
          });
        } else {
          message.info("Product does not have variation");
        }
      } else {
        message.info("Product does not have variation");
      }
    } catch {
      message.error("Failed to fetch variations");
    }
  };

  const handleShowAddVariation = async (product) => {
    setSelectedProduct(product);
    addForm.setFieldsValue({
      productId: product.id,
      product_name: product.product_name,
      fabrics: null,
      fabricColors: [],
      sizes: null,
      price_euro: 0,
      factory_price: 0,
    });

    try {
      await Promise.all([
        fetchFabricsForProduct(product.id),
        fetchSizesFromBackend(),
      ]);
      // reset color options for new open
      setAllowedFabricColors([]);
      setAddModalVisible(true);
    } catch (error) {
      message.error("Failed to load variation data");
      console.error(error);
    }
  };

  const handleSaveAddVariation = async () => {
    try {
      const values = await addForm.validateFields();
      const fabricId = values.fabrics ? Number(values.fabrics) : null;
      const sizeId = values.sizes ? Number(values.sizes) : null;
      const colorIds = values.fabricColors || [];

      const payload = {
        productId: Number(values.productId),
        types: ["Non Ottoman"],
        ottomanBase: 0,
        cloudBase: 0,
      };

      if (fabricId) {
        payload.fabricIds = [fabricId];
        if (colorIds.length > 0) {
          payload.fabricColorIds = { [fabricId]: colorIds };
        }
      }

      if (sizeId) {
        payload.sizeIds = [sizeId];
        payload.priceMap = { [sizeId]: Number(values.price_euro) };
        if (userRole === "super admin") {
          payload.costPriceMap = { [sizeId]: Number(values.factory_price) };
        }
      } else if (fabricId) {
        payload.priceMap = { [fabricId]: Number(values.price_euro) };
        if (userRole === "super admin") {
          payload.costPriceMap = { [fabricId]: Number(values.factory_price) };
        }
      }

      const { data } = await axiosInstance.post(
        "/admin/product-variation",
        payload
      );

      if (data.success) {
        const variationsCreated = Math.max(
          colorIds.length > 0 ? colorIds.length : 1,
          sizeId ? 1 : 1
        );

        message.success(
          `Successfully created ${variationsCreated} variation(s)!`
        );
        setAddModalVisible(false);
        addForm.resetFields();

        // update count locally
        setProducts((prev) =>
          prev.map((p) =>
            p.id === selectedProduct.id
              ? {
                  ...p,
                  variationCount: (p.variationCount || 0) + variationsCreated,
                }
              : p
          )
        );
      } else {
        message.error(data.message || "Failed to add variation.");
      }
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Failed to add variation."
      );
    }
  };

  const handleFabricChange = (fabricId) => {
    const colorsForFabric = allFabricColors
      .filter((c) => c.fabricId === fabricId)
      .map((c) => c.id);

    setAllowedFabricColors(
      allFabricColors.filter((c) => c.fabricId === fabricId)
    );

    // auto-select all colors for that fabric
    addForm.setFieldsValue({
      fabricColors: colorsForFabric,
    });
  };

  const handleDeleteAllVariations = (product) => {
    Modal.confirm({
      title: `Delete all variations for "${product.product_name}"?`,
      content:
        "This will permanently delete every variation for this product. The product itself will remain.",
      okText: "Delete All",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        setDeletingId(product.id);
        try {
          const { data } = await axiosInstance.delete(
            `/admin/product-variation/${product.id}?all=true`
          );

          if (data.success) {
            message.success("All variations deleted.");
            setProducts((prev) =>
              prev.map((p) =>
                p.id === product.id ? { ...p, variationCount: 0 } : p
              )
            );
          } else {
            message.error(data.message || "Failed to delete variations.");
          }
        } catch (e) {
          console.error(e);
          message.error(
            e?.response?.data?.message || "Failed to delete variations."
          );
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  // ---------- table helpers ----------
  const filteredProducts = products.filter(
    (p) =>
      !searchTerm ||
      p.id.toString().includes(searchTerm) ||
      p.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (sortType === "id-asc") filteredProducts.sort((a, b) => a.id - b.id);
  if (sortType === "id-desc") filteredProducts.sort((a, b) => b.id - a.id);
  if (sortType === "name-asc")
    filteredProducts.sort((a, b) =>
      a.product_name.localeCompare(b.product_name)
    );
  if (sortType === "name-desc")
    filteredProducts.sort((a, b) =>
      b.product_name.localeCompare(a.product_name)
    );

  const columns = [
    {
      title: "No",
      key: "srno",
      width: 60,
      render: (_, __, index) => (
        <div className="text-center font-medium text-gray-600">
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </div>
      ),
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      ellipsis: true,
      render: (name) => <div className="font-medium text-gray-900">{name}</div>,
    },
    {
      title: "Variation Count",
      key: "variation_count",
      width: 140,
      render: (_, rec) => (
        <div className="text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 border border-blue-200">
            {rec.variationCount || 0}
          </span>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 450,
      render: (_, rec) => (
        <Space>
          <Button
            className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white font-medium"
            onClick={() => handleViewVariations(rec)}
          >
            View Variations
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleShowAddVariation(rec)}
            className="bg-green-500 hover:bg-green-600 border-green-500 font-medium"
          >
            Add Variation
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === rec.id}
            disabled={deletingId === rec.id}
            onClick={() => handleDeleteAllVariations(rec)}
            className="font-medium"
          >
            Delete All
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Product Variations
            </h1>
            <p className="text-gray-600">
              Manage product variations, fabrics, and sizes
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Products
                </label>
                <Input
                  placeholder="Search by ID or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              {/* Sort */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Sort By
                </label>
                <Select
                  placeholder="Sort products..."
                  value={sortType}
                  onChange={setSortType}
                  className="w-full h-10 rounded-lg"
                  allowClear
                >
                  <Option value="id-asc">Product ID - Ascending</Option>
                  <Option value="id-desc">Product ID - Descending</Option>
                  <Option value="name-asc">Product Name - A to Z</Option>
                  <Option value="name-desc">Product Name - Z to A</Option>
                </Select>
              </div>

              {/* Summary */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {filteredProducts.length}
                  </div>
                  <div className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                    {products.reduce(
                      (sum, p) => sum + (p.variationCount || 0),
                      0
                    )}{" "}
                    variations
                  </div>
                </div>
              </div>

              {/* Quick info */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  Quick Info
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm text-gray-600">
                    {
                      products.filter((p) => (p.variationCount || 0) > 0).length
                    }{" "}
                    products with variations
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <Table
              dataSource={filteredProducts}
              columns={columns}
              rowKey="id"
              // IMPORTANT: no horizontal scroll, so the table uses full width (no empty right gap)
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: filteredProducts.length,
                showQuickJumper: true,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} products`,
              }}
              onChange={(pager) => {
                setPagination((prev) => ({
                  ...prev,
                  current: pager.current || prev.current,
                  pageSize: pager.pageSize || prev.pageSize,
                }));
              }}
              rowClassName={() =>
                "hover:bg-gray-50 transition-colors duration-200"
              }
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">
                      No products found
                    </div>
                    <div className="text-sm text-gray-400">
                      Try adjusting your search or filters
                    </div>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Add Variation Modal */}
      <Modal
        title={
          <div className="text-lg font-semibold text-gray-900">
            Add New Variation{" "}
            {selectedProduct && `- ${selectedProduct.product_name}`}
          </div>
        }
        open={addModalVisible}
        onOk={handleSaveAddVariation}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        width={600}
        okText="Create Variation"
        cancelText="Cancel"
        okButtonProps={{
          className: "bg-blue-500 hover:bg-blue-600 border-blue-500",
        }}
      >
        <Form form={addForm} layout="vertical" className="px-1">
          <Form.Item name="productId" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="product_name" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <div className="grid grid-cols-1 gap-4">
            <Form.Item
              label={<span className="font-medium text-gray-700">Fabric</span>}
              name="fabrics"
            >
              <Select
                placeholder="Select fabric (optional)"
                onChange={handleFabricChange}
                className="w-full rounded-lg"
                optionLabelProp="label"
                notFoundContent={
                  fabrics.length === 0
                    ? "No fabrics available for this product"
                    : null
                }
              >
                {fabrics.map((f) => (
                  <Option key={f.value} value={f.value} label={f.label}>
                    <div className="flex items-center">
                      {f.image_url && (
                        <img
                          src={f.image_url}
                          alt={f.label}
                          className="w-8 h-8 mr-3 rounded border"
                        />
                      )}
                      <span>{f.label}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {addForm.getFieldValue("fabrics") && (
              <Form.Item
                label={
                  <span className="font-medium text-gray-700">
                    Fabric Colors
                  </span>
                }
                name="fabricColors"
                rules={[
                  {
                    required: !!addForm.getFieldValue("fabrics"),
                    message: "Please select at least one color",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select fabric colors"
                  className="w-full rounded-lg"
                  optionLabelProp="label"
                >
                  {allowedFabricColors.map((c) => (
                    <Option key={c.id} value={c.id} label={c.color_name}>
                      <div className="flex items-center">
                        {c.image_url && (
                          <img
                            src={c.image_url}
                            alt={c.color_name}
                            className="w-8 h-8 mr-3 rounded-full border"
                          />
                        )}
                        <span>{c.color_name}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              label={<span className="font-medium text-gray-700">Size</span>}
              name="sizes"
            >
              <Select
                placeholder="Select size (optional)"
                className="w-full rounded-lg"
                notFoundContent={sizes.length === 0 ? "No sizes available" : null}
              >
                {sizes.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {userRole === "super admin" && (
              <Form.Item
                label={
                  <span className="font-medium text-gray-700">
                    Cost Price (€)
                  </span>
                }
                name="factory_price"
                initialValue={0}
                rules={[{ required: true, message: "Please enter the cost price" }]}
              >
                <InputNumber min={0} step={0.01} className="w-full rounded-lg" />
              </Form.Item>
            )}

            <Form.Item
              label={
                <span className="font-medium text-gray-700">Sale Price (€)</span>
              }
              name="price_euro"
              initialValue={0}
              rules={[{ required: true, message: "Please enter the sale price" }]}
            >
              <InputNumber min={0} step={0.01} className="w-full rounded-lg" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Spin>
  );
};

export default ProductVariations;
